import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const AlterarSenha = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Verificar se é definição inicial (vem do email)
  const isDefinicaoInicial = searchParams.get('token') && searchParams.get('email');
  
  const [formData, setFormData] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmarSenha: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    senhaAtual: false,
    novaSenha: false,
    confirmarSenha: false
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // Redirecionar para página de definir senha se for definição inicial
  useEffect(() => {
    if (isDefinicaoInicial) {
      const token = searchParams.get('token');
      const email = searchParams.get('email');
      navigate(`/definir-senha?token=${token}&email=${email}`);
    }
  }, [isDefinicaoInicial, searchParams, navigate]);

  const alterarSenhaMutation = useMutation(
    (data) => api.put('/auth/alterar-senha', data),
    {
      onSuccess: () => {
        setSuccess(true);
        setFormData({ senhaAtual: '', novaSenha: '', confirmarSenha: '' });
        setErrors({});
        setTimeout(() => setSuccess(false), 5000);
      },
      onError: (error) => {
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        } else {
          setErrors({ geral: error.response?.data?.message || 'Erro ao alterar senha' });
        }
      }
    }
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.senhaAtual) {
      newErrors.senhaAtual = 'Senha atual é obrigatória';
    }

    if (!formData.novaSenha) {
      newErrors.novaSenha = 'Nova senha é obrigatória';
    } else if (formData.novaSenha.length < 6) {
      newErrors.novaSenha = 'Nova senha deve ter pelo menos 6 caracteres';
    }

    if (!formData.confirmarSenha) {
      newErrors.confirmarSenha = 'Confirmação de senha é obrigatória';
    } else if (formData.novaSenha !== formData.confirmarSenha) {
      newErrors.confirmarSenha = 'Senhas não coincidem';
    }

    if (formData.senhaAtual === formData.novaSenha) {
      newErrors.novaSenha = 'Nova senha deve ser diferente da senha atual';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    alterarSenhaMutation.mutate({
      senhaAtual: formData.senhaAtual,
      novaSenha: formData.novaSenha
    });
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="card">
        <div className="card-body">
          <div className="text-center mb-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 mb-4">
              <Lock className="h-6 w-6 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Alterar Senha</h1>
            <p className="text-gray-600 mt-2">
              Digite sua senha atual e a nova senha desejada
            </p>
          </div>

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">
                    Senha alterada com sucesso!
                  </p>
                </div>
              </div>
            </div>
          )}

          {errors.geral && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">
                    {errors.geral}
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Senha Atual *
              </label>
              <div className="relative">
                <input
                  type={showPasswords.senhaAtual ? 'text' : 'password'}
                  name="senhaAtual"
                  value={formData.senhaAtual}
                  onChange={handleInputChange}
                  className={`input-field pr-10 ${errors.senhaAtual ? 'border-red-300' : ''}`}
                  placeholder="Digite sua senha atual"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('senhaAtual')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPasswords.senhaAtual ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.senhaAtual && (
                <p className="mt-1 text-sm text-red-600">{errors.senhaAtual}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nova Senha *
              </label>
              <div className="relative">
                <input
                  type={showPasswords.novaSenha ? 'text' : 'password'}
                  name="novaSenha"
                  value={formData.novaSenha}
                  onChange={handleInputChange}
                  className={`input-field pr-10 ${errors.novaSenha ? 'border-red-300' : ''}`}
                  placeholder="Digite sua nova senha"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('novaSenha')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPasswords.novaSenha ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.novaSenha && (
                <p className="mt-1 text-sm text-red-600">{errors.novaSenha}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirmar Nova Senha *
              </label>
              <div className="relative">
                <input
                  type={showPasswords.confirmarSenha ? 'text' : 'password'}
                  name="confirmarSenha"
                  value={formData.confirmarSenha}
                  onChange={handleInputChange}
                  className={`input-field pr-10 ${errors.confirmarSenha ? 'border-red-300' : ''}`}
                  placeholder="Confirme sua nova senha"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirmarSenha')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPasswords.confirmarSenha ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmarSenha && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmarSenha}</p>
              )}
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={alterarSenhaMutation.isLoading}
                className="w-full btn-primary"
              >
                {alterarSenhaMutation.isLoading ? (
                  <div className="flex items-center justify-center">
                    <LoadingSpinner />
                    <span className="ml-2">Alterando...</span>
                  </div>
                ) : (
                  'Alterar Senha'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              A senha deve ter pelo menos 6 caracteres e ser diferente da senha atual.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlterarSenha;
